// src/components/paywall/PaywallModal.jsx
// COMPLETE FIXED VERSION with Group Mode Support + FREE CODE SYSTEM + SSR SAFETY + PRICE LABEL FIXES

import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../auth/auth-components';
import creditService from '../../services/credit-service';
import stripeService from '../../services/stripe-service';
import { UNIFIED_CREDIT_PACKAGES, FAN_TYPE_MAPPINGS } from '../../utils/unified-credit-config';
import { logger } from '../../utils/logger';

const hasWindow = typeof window !== 'undefined';
const hasDocument = typeof document !== 'undefined';
const getHref = () => (hasWindow && window.location ? window.location.href : '');
const safeLocalStorage = hasWindow ? window.localStorage : null;
const safeSessionStorage = hasWindow ? window.sessionStorage : null;

export const PaywallModal = ({
  isOpen,
  onClose,
  feature,
  fanType,
  onPurchaseSuccess,
  onConfirm,
  gameData,
  setGameData,
  storeUserFlowBeforePayment
}) => {
  const { user } = useAuth();

  // State
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [checkingCredits, setCheckingCredits] = useState(true);
  const [showCreditModal, setShowCreditModal] = useState(false);
  const [userCredits, setUserCredits] = useState({ dedicated: 0, expert: 0 });
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Free code state
  const [freeCodeModal, setFreeCodeModal] = useState({
    isOpen: false,
    code: '',
    processing: false
  });
  const [freeCodeAccess, setFreeCodeAccess] = useState(null);

  // Packages
  const creditPackages =
    UNIFIED_CREDIT_PACKAGES[fanType] || UNIFIED_CREDIT_PACKAGES.dedicated;

  // Group mode?
  const isGroupMode = gameData?.groupMode && gameData?.groupId;

  // Feature configs
  const FEATURE_CONFIG = {
    dedicatedFan: {
      name: 'Dedicated Fan',
      emoji: '🔥',
      color: '#dc2626',
      description:
        'Get detailed team stats, player analysis, and game predictions',
      features: [
        'Dedicated Fan Analysis Mode',
        'Advanced Team Insights',
        'Historical Performance Data',
        'Cancel anytime'
      ]
    },
    expertAnalyst: {
      name: 'Expert Analyst',
      emoji: '🧠',
      color: '#7c3aed',
      description:
        'Unlock the complete analytical experience with insider insights',
      features: [
        'DieHard Fan Mode',
        'All Dedicated Fan features',
        'Advanced Team Insights',
        'Historical Performance Data',
        'Cancel anytime'
      ]
    },
    diehard: {
      name: 'DieHard Fan',
      emoji: '🧠',
      color: '#7c3aed',
      description:
        'Unlock the complete analytical experience with insider insights',
      features: [
        'DieHard Fan Mode',
        'Complete team breakdown',
        'Advanced coaching analysis',
        'All position groups',
        'Premium predictions'
      ]
    }
  };

  const selectedFeature =
    FEATURE_CONFIG[feature] || FEATURE_CONFIG.dedicatedFan;

  /* ------------------------- FREE CODE ------------------------- */
  const handleFreeCodeSubmit = async () => {
    if (!freeCodeModal.code.trim() || !hasWindow) return;

    setFreeCodeModal(prev => ({ ...prev, processing: true }));
    setError(null);

    try {
      if (!window.freeCodeService) {
        throw new Error('Free code service not available');
      }

      const result = await window.freeCodeService.validateAndUseFreeCode(
        freeCodeModal.code,
        user?.email
      );

      if (result.valid) {
        setFreeCodeAccess(result);
        setFreeCodeModal({ isOpen: false, code: '', processing: false });

        const payload = {
          success: true,
          freeAccess: true,
          fanType: result.fanType,
          skipCreditConsumption: true,
          freeCodeUsed: freeCodeModal.code
        };

        onClose?.();
        onConfirm?.(payload);
        onPurchaseSuccess?.(payload);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError('Failed to validate free code');
      console.error('❌ Free code validation error:', err);
    } finally {
      setFreeCodeModal(prev => ({ ...prev, processing: false }));
    }
  };

  // Check pending free code from URL/localStorage
  useEffect(() => {
    if (!isOpen || !user || !hasWindow || !safeLocalStorage) return;

    const checkPendingFreeCode = async () => {
      try {
        const pendingFreeCode = safeLocalStorage.getItem('sbe_pending_free_code');
        if (!pendingFreeCode) return;

        const freeCodeData = JSON.parse(pendingFreeCode);
        logger.success?.('🎁 Found pending free code:', freeCodeData);

        if (window.freeCodeService) {
          const result = await window.freeCodeService.validateAndUseFreeCode(
            freeCodeData.code,
            user.email
          );

          if (result.valid) {
            setFreeCodeAccess(result);
            safeLocalStorage.removeItem('sbe_pending_free_code');

            const payload = {
              success: true,
              freeAccess: true,
              fanType: result.fanType,
              skipCreditConsumption: true,
              freeCodeUsed: freeCodeData.code
            };

            onClose?.();
            onConfirm?.(payload);
            onPurchaseSuccess?.(payload);
          } else {
            setError(`Free code error: ${result.error}`);
            safeLocalStorage.removeItem('sbe_pending_free_code');
          }
        }
      } catch (err) {
        console.error('❌ Error processing pending free code:', err);
        safeLocalStorage.removeItem('sbe_pending_free_code');
      }
    };

    checkPendingFreeCode();
  }, [isOpen, user, onClose, onConfirm, onPurchaseSuccess]);

  /* --------------------- CREDIT CHECK + INIT -------------------- */
  useEffect(() => {
    if (!isOpen || !user) return;

    const checkUserCredits = async () => {
      try {
        setCheckingCredits(true);
        setError(null);

        console.log(
          isGroupMode
            ? '👥 GROUP MODE: Checking user credits for'
            : '👤 SOLO MODE: Checking user credits for',
          feature,
          fanType
        );

        const credits = await creditService.checkBalance(user.uid);
        setUserCredits(credits);

        const backendFanType = FAN_TYPE_MAPPINGS.toBackend(fanType);
        const requiredCreditType =
          backendFanType === 'expert' ? 'expert' : 'dedicated';
        const hasRequiredCredits = (credits?.[requiredCreditType] || 0) > 0;

        logger.credits?.('🎯 FIXED Credit check result:', {
          mode: isGroupMode ? 'GROUP' : 'SOLO',
          feature,
          frontendFanType: fanType,
          backendFanType,
          requiredCreditType,
          availableCredits: credits?.[requiredCreditType],
          hasRequiredCredits,
          allCredits: credits
        });

        setShowCreditModal(hasRequiredCredits);
      } catch (err) {
        logger.error?.('Credit check failed:', err);
        setError('Unable to check credits. Please try again.');
        setUserCredits({ dedicated: 0, expert: 0 });
        setShowCreditModal(false);
      } finally {
        setCheckingCredits(false);
      }
    };

    checkUserCredits();

    // Auto-select popular or first package
    const popularPkg = creditPackages.find(pkg => pkg.popular);
    setSelectedPackage(popularPkg || creditPackages[0] || null);
  }, [isOpen, user, fanType, feature, isGroupMode, creditPackages]);

  /* --------------------- SCROLL MANAGEMENT --------------------- */
  useEffect(() => {
    if (!hasDocument) return;

    if (isOpen) {
      document.body.scrollTop = 0;
      document.documentElement && (document.documentElement.scrollTop = 0);
      hasWindow && window.scrollTo?.(0, 0);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      if (hasDocument) document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  /* ---------------------- CREDIT CONFIRMATION ------------------- */
  const handleCreditConfirm = useCallback(
    result => {
      logger.credits?.('Credit usage result:', result);

      if (result.freeAccess) {
        setShowCreditModal(false);
        onClose?.();

        const payload = {
          success: true,
          freeAccess: true,
          fanType: result.fanType,
          skipCreditConsumption: true,
          freeCodeUsed: result.freeCodeUsed
        };

        onConfirm?.(payload);
        onPurchaseSuccess?.(payload);
        return;
      }

      if (result.success && result.creditUsed) {
        setShowCreditModal(false);
        onClose?.();
        onConfirm?.(result);
        onPurchaseSuccess?.({
          success: true,
          creditUsed: true,
          skipCreditConsumption: false,
          ...result
        });
      } else if (result.needsPurchase) {
        setShowCreditModal(false); // leave purchase modal open
      }
    },
    [onClose, onConfirm, onPurchaseSuccess]
  );

  /* -------- ENHANCED STORAGE BEFORE PAYMENT (SSR-SAFE) --------- */
  const enhancedStoreUserFlowBeforePayment = useCallback(
    (backendFanType, selectedPkg) => {
      try {
        logger.credits?.(
          '💾 ENHANCED: Storing user flow before payment with security flags'
        );

        const attemptId = `payment_${Date.now()}_${Math.random()
          .toString(36)
          .substr(2, 9)}`;

        const gameDataWithPaymentFlag = {
          ...gameData,
          fanType: backendFanType,
          activeTab: 'breakdown',
          isCreditPurchase: true,
          creditPackage: selectedPkg,
          _paymentFlowActive: true,
          _redirectingToStripe: true,
          _paymentStartTime: Date.now(),
          _paymentAttemptId: attemptId
        };

        if (typeof setGameData === 'function') {
          setGameData(gameDataWithPaymentFlag);
        }

        if (!safeLocalStorage || !hasWindow) return;

        const completeDataToStore = {
          ...gameDataWithPaymentFlag,
          timestamp: Date.now(),
          userAgent: navigator.userAgent.substring(0, 100),
          originalUrl: getHref()
        };

        logger.credits?.(
          '💾 Storing game data in localStorage before payment (NOT Firebase)'
        );

        safeLocalStorage.setItem(
          'sbe-temp-game-data',
          JSON.stringify(completeDataToStore)
        );
        safeLocalStorage.setItem('sbe-temp-fan-type', backendFanType);
        safeLocalStorage.setItem('sbe-temp-current-step', 'game');
        safeLocalStorage.setItem(
          'sbe-payment-timestamp',
          Date.now().toString()
        );
        safeLocalStorage.setItem('sbe-payment-attempt-id', attemptId);

        safeSessionStorage?.setItem('stripe-payment-active', 'true');
        safeSessionStorage?.setItem(
          'stripe-payment-start',
          Date.now().toString()
        );
        safeSessionStorage?.setItem('payment-security-token', attemptId);

        storeUserFlowBeforePayment?.();

        logger.credits?.('✅ Enhanced payment flow storage completed');
      } catch (err) {
        logger.error?.('❌ Enhanced payment flow storage failed:', err);
        storeUserFlowBeforePayment?.();
      }
    },
    [gameData, setGameData, storeUserFlowBeforePayment]
  );

  /* --------------------------- PURCHASE ------------------------ */
  const handlePurchase = useCallback(async () => {
    if (loading || !selectedPackage) return;

    setLoading(true);
    setError(null);

    try {
      logger.credits?.('💳 Starting credit purchase flow');

      const backendFanType = FAN_TYPE_MAPPINGS.toBackend(fanType);

      // Set payment flow flags ASAP
      const gameDataWithPaymentFlag = {
        ...gameData,
        _paymentFlowActive: true,
        _redirectingToStripe: true
      };
      typeof setGameData === 'function' && setGameData(gameDataWithPaymentFlag);

      // Persist flow
      enhancedStoreUserFlowBeforePayment(backendFanType, selectedPackage);

      // Extra local storage (SSR safe)
      if (safeLocalStorage) {
        const completeDataToStore = {
          ...gameData,
          fanType: backendFanType,
          activeTab: 'breakdown',
          isCreditPurchase: true,
          creditPackage: selectedPackage,
          timestamp: Date.now(),
          _paymentFlowActive: true
        };
        safeLocalStorage.setItem(
          'sbe-temp-game-data',
          JSON.stringify(completeDataToStore)
        );
        safeLocalStorage.setItem('sbe-temp-fan-type', backendFanType);
        safeLocalStorage.setItem('sbe-temp-current-step', 'game');
        safeLocalStorage.setItem(
          'sbe-payment-timestamp',
          Date.now().toString()
        );
      }

      // Start Stripe checkout
      const sessionData = await stripeService.createCreditPayment({
        priceId: selectedPackage.priceId,
        fanType: backendFanType,
        returnUrl: getHref()
      });

      logger.credits?.('🔗 Redirecting to Stripe checkout');

      if (sessionData.url && hasWindow) {
        window.location.href = sessionData.url;
      } else {
        throw new Error('No checkout URL received from Stripe');
      }
    } catch (err) {
      logger.error?.('❌ Purchase error:', err);
      setError(err.message || 'Purchase failed. Please try again.');
      setLoading(false);

      // Clear payment flags on error
      typeof setGameData === 'function' &&
        setGameData(prev => ({
          ...prev,
          _paymentFlowActive: false,
          _redirectingToStripe: false
        }));
    }
  }, [
    loading,
    selectedPackage,
    fanType,
    gameData,
    enhancedStoreUserFlowBeforePayment,
    setGameData
  ]);

  /* --------------------------- RENDER -------------------------- */
  if (checkingCredits) {
    logger.credits?.('PaywallModal: Checking credits...', {
      isOpen,
      user: user?.uid,
      feature,
      isGroupMode
    });

    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            textAlign: 'center'
          }}
        >
          <div style={{ fontSize: '24px', marginBottom: '16px' }}>💳</div>
          <div>Checking your {fanType} credits...</div>
        </div>
      </div>
    );
  }

  // Credit confirmation modal (user already has the right credit type)
  if (showCreditModal) {
    const backendFanType = FAN_TYPE_MAPPINGS.toBackend(fanType);
    const requiredCreditType =
      backendFanType === 'expert' ? 'expert' : 'dedicated';
    const availableCredits = userCredits[requiredCreditType];

    logger.credits?.('🎯 FIXED - User has correct credit type available:', {
      frontendFanType: fanType,
      backendFanType,
      requiredCreditType,
      availableCredits,
      totalCredits: userCredits,
      isGroupMode
    });

    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '32px',
            textAlign: 'center',
            maxWidth: '400px'
          }}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>
            {isGroupMode ? '👥' : '💳'}
          </div>
          <h2 style={{ marginBottom: '16px' }}>
            {isGroupMode
              ? `Use ${fanType} Credit for Group?`
              : `Use ${fanType} Credit?`}
          </h2>
          {isGroupMode && (
            <p style={{ color: '#6b7280', marginBottom: '8px', fontSize: '14px' }}>
              Group: "{gameData.groupId}"
            </p>
          )}
          <p style={{ color: '#6b7280', marginBottom: '8px' }}>
            You have {availableCredits} {fanType} credit
            {availableCredits !== 1 ? 's' : ''} available.
          </p>
          <p style={{ color: '#6b7280', marginBottom: '24px' }}>
            Would you like to use 1 credit for this {fanType} analysis?
          </p>

          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => handleCreditConfirm({ needsPurchase: true })}
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: '#f3f4f6',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Buy More Credits
            </button>
            <button
              onClick={() =>
                handleCreditConfirm({
                  success: true,
                  creditUsed: true,
                  fanType: backendFanType
                })
              }
              style={{
                flex: 1,
                padding: '12px',
                backgroundColor: selectedFeature.color,
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer'
              }}
            >
              Use 1 {fanType} Credit
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Not open
  if (!isOpen) return null;

  // No packages
  if (!creditPackages || creditPackages.length === 0) {
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          zIndex: 1000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '20px'
        }}
      >
        <div
          style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '24px',
            textAlign: 'center',
            maxWidth: '400px'
          }}
        >
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
          <h3 style={{ marginBottom: '8px' }}>Configuration Error</h3>
          <p style={{ color: '#6b7280', marginBottom: '16px' }}>
            Unable to load {fanType} credit packages. Please check configuration.
          </p>
          <button
            onClick={onClose}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              cursor: 'pointer'
            }}
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // Main modal
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        zIndex: 1000,
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'center',
        padding: '20px',
        overflowY: 'auto'
      }}
      onClick={e => {
        if (e.target === e.currentTarget && onClose) onClose();
      }}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: 0,
          maxWidth: '480px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
          position: 'relative',
          marginTop: '5vh'
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            background: `linear-gradient(135deg, ${selectedFeature.color}, ${selectedFeature.color}dd)`,
            color: 'white',
            padding: '32px 30px',
            borderRadius: '20px 20px 0 0',
            textAlign: 'center',
            position: 'relative'
          }}
        >
          {onClose && (
            <button
              onClick={onClose}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                background: 'rgba(255, 255, 255, 0.2)',
                border: 'none',
                borderRadius: '50%',
                width: '40px',
                height: '40px',
                color: 'white',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'background-color 0.2s'
              }}
              onMouseOver={e =>
                (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.3)')
              }
              onMouseOut={e =>
                (e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)')
              }
            >
              ×
            </button>
          )}

          <div style={{ fontSize: '48px', marginBottom: '16px' }}>
            {isGroupMode ? '👥' : selectedFeature.emoji}
          </div>
          <h2
            style={{
              fontSize: '28px',
              fontWeight: '700',
              margin: '0 0 12px 0'
            }}
          >
            {isGroupMode
              ? `Group ${selectedFeature.name}`
              : selectedFeature.name}
          </h2>
          <p
            style={{
              fontSize: '16px',
              opacity: 0.9,
              margin: 0,
              lineHeight: 1.5
            }}
          >
            {isGroupMode
              ? `Purchase ${fanType} credits to participate in group "${gameData.groupId}"`
              : selectedFeature.description}
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '30px' }}>
          {error && (
            <div
              style={{
                backgroundColor: '#fee2e2',
                border: '1px solid #fecaca',
                color: '#dc2626',
                padding: '16px',
                borderRadius: '12px',
                marginBottom: '24px',
                fontSize: '14px',
                textAlign: 'center'
              }}
            >
              ⚠️ {error}
            </div>
          )}

          {isGroupMode && (
            <div
              style={{
                backgroundColor: '#f0f9ff',
                border: '1px solid #0ea5e9',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '16px',
                fontSize: '14px',
                textAlign: 'center',
                color: '#000000'
              }}
            >
              👥 <strong>Group Member:</strong> You're joining group "
              {gameData.groupId}". Each member needs their own {fanType} credits
              to participate.
            </div>
          )}

          {/* Free code card */}
          <div style={{ marginBottom: '24px' }}>
            <h3
              style={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '16px',
                textAlign: 'center'
              }}
            >
              🎁 Have a Free Access Code?
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div
                style={{
                  border: '2px solid #10b981',
                  borderRadius: '12px',
                  padding: '16px',
                  transition: 'all 0.2s',
                  backgroundColor: '#f0fdf4',
                  position: 'relative'
                }}
              >
                <div
                  style={{
                    position: 'absolute',
                    top: '-10px',
                    right: '16px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    padding: '4px 12px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: 600
                  }}
                >
                  FREE
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#111827',
                          marginBottom: '4px'
                        }}
                      >
                        Free {fanType} Access
                      </div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                        Enter 8-character code
                      </div>
                      <div
                        style={{
                          fontSize: '12px',
                          color: '#059669',
                          fontWeight: 600,
                          marginTop: '4px'
                        }}
                      >
                        ✨ No payment required
                      </div>
                    </div>

                    <div
                      style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: '#10b981'
                      }}
                    >
                      FREE
                    </div>
                  </div>

                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '10px',
                      marginTop: '8px'
                    }}
                  >
                    <input
                      type="text"
                      placeholder="8-CHAR-CODE"
                      value={freeCodeModal.code}
                      onChange={e =>
                        setFreeCodeModal(prev => ({
                          ...prev,
                          code: e.target.value
                            .toUpperCase()
                            .replace(/[^A-Z0-9]/g, '')
                            .slice(0, 8)
                        }))
                      }
                      style={{
                        width: '100%',
                        padding: '12px',
                        border: '2px solid #d1fae5',
                        borderRadius: '8px',
                        fontSize: '16px',
                        textAlign: 'center',
                        fontWeight: 600,
                        letterSpacing: '2px',
                        fontFamily: 'monospace',
                        backgroundColor: 'white',
                        color: '#065f46'
                      }}
                      maxLength={8}
                      disabled={freeCodeModal.processing}
                    />
                    <button
                      onClick={handleFreeCodeSubmit}
                      disabled={
                        !freeCodeModal.code.trim() ||
                        freeCodeModal.processing ||
                        freeCodeModal.code.length !== 8
                      }
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: freeCodeModal.processing
                          ? '#9ca3af'
                          : !freeCodeModal.code.trim() ||
                            freeCodeModal.code.length !== 8
                          ? '#d1d5db'
                          : '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontSize: '16px',
                        fontWeight: 600,
                        cursor:
                          freeCodeModal.processing ||
                          !freeCodeModal.code.trim() ||
                          freeCodeModal.code.length !== 8
                            ? 'not-allowed'
                            : 'pointer'
                      }}
                    >
                      {freeCodeModal.processing
                        ? '⏳ Checking...'
                        : '🎁 Redeem Free Access'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Divider */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginBottom: '24px'
            }}
          >
            <div style={{ flex: 1, height: '1px', backgroundColor: '#e5e7eb' }} />
            <span
              style={{
                margin: '0 16px',
                color: '#6b7280',
                fontSize: '14px',
                fontWeight: 500
              }}
            >
              or purchase credits
            </span>
            <div style={{ flex: 1, height: '1px', backgroundColor: '#e5e7eb' }} />
          </div>

          {/* Package selection */}
          <div style={{ marginBottom: '24px' }}>
            <h3
              style={{
                fontSize: '18px',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '16px',
                textAlign: 'center'
              }}
            >
              💳 Choose Your {fanType} Credit Package{' '}
              {isGroupMode ? '(Individual Purchase)' : ''}
            </h3>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {creditPackages.map(pkg => (
                <div
                  key={pkg.priceId}
                  onClick={() => {
                    setSelectedPackage(pkg);
                    setError(null);
                  }}
                  style={{
                    border: `2px solid ${
                      selectedPackage?.priceId === pkg.priceId
                        ? selectedFeature.color
                        : '#e5e7eb'
                    }`,
                    borderRadius: '12px',
                    padding: '16px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    backgroundColor:
                      selectedPackage?.priceId === pkg.priceId
                        ? `${selectedFeature.color}10`
                        : 'white',
                    position: 'relative'
                  }}
                  onMouseOver={e => {
                    if (selectedPackage?.priceId !== pkg.priceId) {
                      e.currentTarget.style.borderColor =
                        selectedFeature.color + '80';
                    }
                  }}
                  onMouseOut={e => {
                    if (selectedPackage?.priceId !== pkg.priceId) {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                    }
                  }}
                >
                  {pkg.popular && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '-10px',
                        right: '16px',
                        backgroundColor: '#f59e0b',
                        color: 'white',
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: 600
                      }}
                    >
                      POPULAR
                    </div>
                  )}

                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: '16px',
                          fontWeight: 600,
                          color: '#111827',
                          marginBottom: '4px'
                        }}
                      >
                        {pkg.credits} {fanType} Credit
                        {pkg.credits > 1 ? 's' : ''}
                      </div>
                      <div style={{ fontSize: '14px', color: '#6b7280' }}>
                        {pkg.name}
                        {pkg.credits > 1 && (
                          <span
                            style={{
                              marginLeft: '8px',
                              color: selectedFeature.color,
                              fontWeight: 600
                            }}
                          >
                            ${((pkg.amount / 100) / pkg.credits).toFixed(2)} each
                          </span>
                        )}
                      </div>
                      {pkg.description && (
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#059669',
                            fontWeight: 600,
                            marginTop: '4px'
                          }}
                        >
                          ✨ {pkg.description}
                        </div>
                      )}
                    </div>

                    <div
                      style={{
                        fontSize: '24px',
                        fontWeight: 'bold',
                        color: '#111827'
                      }}
                    >
                      {pkg.price || `$${(pkg.amount / 100).toFixed(2)}`}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Feature list */}
          <div style={{ textAlign: 'left', marginBottom: '25px' }}>
            <h4
              style={{
                fontSize: '16px',
                fontWeight: 600,
                color: '#374151',
                marginBottom: '15px',
                textAlign: 'center'
              }}
            >
              What's Included:
            </h4>
            <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
              {selectedFeature.features.map((featureItem, i) => (
                <li
                  key={i}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    marginBottom: '10px',
                    fontSize: '14px',
                    color: '#374151'
                  }}
                >
                  <span
                    style={{
                      color: '#10b981',
                      marginRight: '10px',
                      fontWeight: 600
                    }}
                  >
                    ✓
                  </span>
                  {featureItem}
                </li>
              ))}
            </ul>
          </div>

          {/* Purchase button */}
          <button
            onClick={handlePurchase}
            disabled={loading || !selectedPackage}
            style={{
              width: '100%',
              padding: '18px 25px',
              backgroundColor:
                loading || !selectedPackage ? '#9ca3af' : selectedFeature.color,
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: 600,
              cursor: loading || !selectedPackage ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s',
              boxShadow:
                loading || !selectedPackage
                  ? 'none'
                  : '0 4px 12px rgba(0, 0, 0, 0.15)'
            }}
            onMouseOver={e => {
              if (!loading && selectedPackage) {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow =
                  '0 6px 20px rgba(0, 0, 0, 0.2)';
              }
            }}
            onMouseOut={e => {
              if (!loading && selectedPackage) {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow =
                  '0 4px 12px rgba(0, 0, 0, 0.15)';
              }
            }}
          >
            {loading ? (
              <span
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <span
                  style={{
                    width: '20px',
                    height: '20px',
                    border: '2px solid #ffffff40',
                    borderTop: '2px solid white',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }}
                />
                Processing...
              </span>
            ) : selectedPackage ? (
              `Buy ${selectedPackage.credits} ${fanType} Credit${
                selectedPackage.credits > 1 ? 's' : ''
              } - $${(selectedPackage.amount / 100).toFixed(2)}`
            ) : (
              'Select Package'
            )}
          </button>

          {/* Package summary */}
          {selectedPackage && (
            <div
              style={{
                backgroundColor: '#f8fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '8px',
                padding: '16px',
                marginTop: '20px',
                fontSize: '14px',
                textAlign: 'center',
                color: '#6b7280'
              }}
            >
              <div style={{ marginBottom: '8px', fontWeight: 600, color: '#374151' }}>
                📦 You'll get: {selectedPackage.credits} {fanType} credit
                {selectedPackage.credits > 1 ? 's' : ''} for {selectedFeature.name}
                {isGroupMode && (
                  <span style={{ display: 'block', fontSize: '12px', marginTop: '4px' }}>
                    (Individual purchase - each group member buys their own credits)
                  </span>
                )}
              </div>
              <div style={{ fontSize: '12px', lineHeight: 1.5 }}>
                🔒 Secure payment • ⚡ Instant access • 💳 One-time purchase
              </div>
            </div>
          )}
        </div>

        {/* Spinner keyframes */}
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default PaywallModal;
